<script setup lang='ts'>
import { computed, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faXmark, faCamera } from '@fortawesome/pro-regular-svg-icons'
import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
import Typography from '@/components/common/Typography.vue'

const props = defineProps<{
  isFollowingColorPair?: boolean
  caption?: string
  copyright?: string
}>()

const imageCaption = ref(null)
const isOpen = ref<boolean>(false)

onClickOutside(imageCaption, () => isOpen.value = false)

const colorPairClass = computed(() => props.isFollowingColorPair ? 'follow-color-pair' : '')
// IbexaRichTextField returns this string if rich text is "empty"
const isCaptionEmpty = computed(() => props.caption === '<?xml version="1.0" encoding="UTF-8"?>\n' || !props.caption)
</script>

<template>
  <div
    v-if="!isCaptionEmpty || !!copyright"
    ref="imageCaption"
  >
    <CommonButton
      :aria-label="(isOpen ? 'Lukk' : 'Åpne') + 'bildebeskrivelse'"
      :is-on-dark="isOpen"
      :decoration="isOpen ? 'outline' : 'fill'"
      shape="icon"
      size="small"
      class="toggle-button"
      @click="isOpen = !isOpen"
    >
      <FontAwesomeIcon :icon="isOpen ? faXmark : faCamera" />
    </CommonButton>
    <Transition name="fade">
      <div
        v-if="isOpen"
        :class="['credits-section', colorPairClass, { 'copyright-only': isCaptionEmpty }]"
        :aria-label="$t('imageCredits')"
      >
        <RichTextRenderer
          v-if="!isCaptionEmpty"
          :rich-text="caption!"
        />
        <Typography
          v-if="copyright"
          variant="caption"
        >
          {{ copyright }}
        </Typography>
      </div>
    </Transition>
  </div>
</template>

<style lang='scss' scoped>
  .credits-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    width: 100%;
    max-height: 100%;
    min-height: 90px;
    padding: 24px 32px;
    background-color: var(--color-pair-color, var(--color-blue-50));
    color: var(--color-pair-background-color, var(--color-white));
    overflow: auto;
    padding-right: 96px;
    opacity: .85;

    :deep(p) {
      font-size: 1rem;
      line-height: 1.5;
      margin: 0;
    }

    :deep(.typography) {
      opacity: 1;
    }

    &.copyright-only {
      flex-direction: row;
      align-items: center;
      gap: 0;
    }

    @include vgs-media-mobile {
      padding: 1.5rem;
      padding-right: 96px;
    }
  }

  .toggle-button {
    position: absolute;
    margin: 28px 32px;
    bottom: 0;
    right: 0;
    z-index: 3;
    opacity: .85;
  }

  /* Fade transition from animations.css */
  .fade-enter-active {
    animation: fade-in .4s ease-out;
  }

  .fade-leave-active {
    animation: fade-in .4s reverse ease-in;
  }
</style>
